import { Autocomplete, Box, Button, Chip, FormControlLabel, Paper, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { selectUserData, selectUserToken } from '../../../../features/userSlice';
import { useTheme } from "@mui/material/styles";
import { ManualExpenseEndPointLogic } from '../../../../logic/Finance/ProviderExpense/ManualExpenseEndpointLogic';
import SimpleBackdrop from '../../../BackDrop/BackDrop';
import ManualExpenseTable from './ManualExpenseTable/ManualExpenseTable';
import { setFilteredManualExpense, setManualExpenseData } from '../../../../features/manualExpenseSlice';

const ManualExpenses = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken);
  const dispatch = useDispatch();
  // const { allDimensions } = useSelector((state: any) => state.activityNotes);
  const initialFetchBy = searchParams.get('fetchBy') || 'Website';
  const initialSelected = searchParams.get('selected') || '';
  const [fetchBy, setFetchBy] = useState(initialFetchBy);
  const [selectedOptions, setSelectedOptions] = useState(
    initialSelected ? initialSelected.split(',') : []
  );
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const siteAndDimensionsList = useSelector(selectUserData);
  const dimensionList = siteAndDimensionsList?.data?.dimensions;
  const siteList = siteAndDimensionsList?.data?.website_permissions;
  const theme = useTheme();

  const SELECT_ALL_OPTION = { value: "select_all", label: "Select All" };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (fetchBy) params.set("fetchBy", fetchBy);
    if (selectedOptions.length > 0) {
      params.set("selected", selectedOptions.join(","));
    } else {
      params.delete("selected");
      dispatch(setManualExpenseData([])); // Clear manual expense data if no selection
      dispatch(setFilteredManualExpense([])); // Clear filtered data if no selection
    }
    // Preserve other params like 'page'
    setSearchParams(params);
    // eslint-disable-next-line
  }, [fetchBy, selectedOptions]);

  const getAutocompleteOptions = () => {
    switch (fetchBy) {
      case "Website":
        return siteList?.map((siteObj: any) => {
          const siteId = Object.keys(siteObj)[0];
          const siteData = siteObj[siteId];
          return {
            value: siteId,
            label: siteData.ui_site_name || siteData.site_url || "Unnamed Website",
          };
        });
      case "Dimension":
        return dimensionList?.map((dimension: string) => ({
          value: dimension,
          label: dimension,
        }));
      case "Organization":
        return [];
      default:
        return [];
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const autocompleteOptions = useMemo(() => getAutocompleteOptions(), [fetchBy, siteList, dimensionList]);
  const allOptionsSelected = selectedOptions.length === autocompleteOptions.length && autocompleteOptions.length > 0;

  useEffect(() => {
    if (initialSelected.length > 0 && autocompleteOptions.length > 0 && !hasFetchedData) {
      handleGetData();
    }
    // eslint-disable-next-line
  }, []);

  const handleGetData = async () => {
    setLoading(true);
    try {
      await ManualExpenseEndPointLogic.getManualExpenseData(oauthTokenHeader, dispatch);
    } catch (error) {
      dispatch({ type: "error", payload: { message: error } });
    } finally {
      setLoading(false);
      setHasFetchedData(true);
    }
  };

  const manualExpenseData = useSelector((state: any) => state.manualExpense.manualExpenseData);

  const manualExpenseLists = useMemo(() => {
    const list = [] as any;
    if (manualExpenseData) {
      Object.keys(manualExpenseData).forEach((websiteSharedUid: string) => {
        const items = (manualExpenseData)[websiteSharedUid];
        if (Array?.isArray(items)) {
          items?.forEach((item: any) => {
            list?.push({
              ...item,
              website_shared_uid: websiteSharedUid,
              website_name: autocompleteOptions.find((opt: any) => opt?.value === websiteSharedUid)?.label || "Unknown Website",
            });
          });
        }
      });
    }
    return list;
  }, [manualExpenseData, autocompleteOptions]);

  useEffect(() => {
    if (manualExpenseLists.length > 0 && selectedOptions.length > 0) {
      const filtered = manualExpenseLists.filter((note: any) => selectedOptions.includes(note.website_shared_uid));
      dispatch(setFilteredManualExpense(filtered));
    } else if (selectedOptions.length === 0) {
      dispatch(setFilteredManualExpense([]));
    }
  }, [manualExpenseLists, selectedOptions, dispatch]);

  // Handler for autocomplete changes
  const handleAutocompleteChange = (_: any, newValue: any[]) => {
    const isSelectAll = newValue.some(
      (opt) => opt.value === SELECT_ALL_OPTION.value
    );

    if (isSelectAll) {
      if (selectedOptions.length === autocompleteOptions.length) {
        // If all are selected, deselect all
        setSelectedOptions([]);
      } else {
        // If not all are selected, select all
        setSelectedOptions(autocompleteOptions.map((option: any) => option.value));
      }
    } else {
      // Normal selection logic
      setSelectedOptions(newValue ? newValue.map((option) => option.value) : []);
    }
    setHasFetchedData(false);
  };

  const getUserList = async (oauthTokenHeader: string) => {
    await ManualExpenseEndPointLogic.getAllUserList(oauthTokenHeader);
    await ManualExpenseEndPointLogic.getTrafficSource(oauthTokenHeader);
  };

  //-- Get All Users
  useEffect(() => {
    if (!oauthTokenHeader) return;
    getUserList(oauthTokenHeader);
  }, [oauthTokenHeader]);

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography fontSize={24}>Manual Expenses</Typography>
            <Typography fontSize={14} color="#00000099" sx={{ mt: 0.5 }}>
              Create new or edit existing Manual Expenses.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mt: { xs: 2, md: 0 } }}>
            <Typography sx={{ mr: 2 }}>Fetch by:</Typography>
            <RadioGroup
              row
              value={fetchBy}
              onChange={(e: any) => {
                const newValue = e.target.value;
                setFetchBy(newValue);
                setSelectedOptions([]); // Reset selected options when fetchBy changes
                if (newValue === "Organization") {
                  // fetchAllOrganizationData();
                }
              }}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <FormControlLabel
                value="Website"
                control={<Radio color="secondary" />}
                data-tracker="420"
                label="Website"
              />
              <FormControlLabel
                value="Dimension"
                control={<Radio color="secondary" />}
                data-tracker="421"
                label="Dimension"
                disabled
              />
              <FormControlLabel
                value="Organization"
                control={<Radio color="secondary" />}
                data-tracker="422"
                label="Organization"
                disabled
              />
            </RadioGroup>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            mt: 3,
            flexWrap: "wrap",
          }}
        >
          <Autocomplete
            multiple
            options={allOptionsSelected ? autocompleteOptions : [SELECT_ALL_OPTION, ...autocompleteOptions]}
            size='small'
            color='secondary'
            data-tracker="419"
            getOptionLabel={(option: any) => option.label}
            isOptionEqualToValue={(option: any, value: any) =>
              option.value === value.value
            }
            value={selectedOptions?.map((val: any) => autocompleteOptions?.find((opt: any) => opt.value === val)).filter(Boolean)}
            onChange={handleAutocompleteChange}
            sx={{ minWidth: 300, flexGrow: 1, maxWidth: "calc(100% - 140px)" }} // Ensure space for the button
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter((option) =>
                option.label.toLowerCase().includes(searchTerm)
              );
            }}
            filterSelectedOptions
            renderTags={(value, getTagProps) =>
              value.map((option: any, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  color="secondary"
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.contrastText,
                    height: 24,
                  }}
                  deleteIcon={<CancelIcon />}
                />
              ))
            }
            renderInput={(params: any) => (
              <TextField
                {...params}
                label="Select Website or Dimension"
                color="secondary"
                InputLabelProps={{ shrink: true }}
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  backgroundColor: option.value === SELECT_ALL_OPTION.value ? "#9C27B00A" : "inherit",
                  color: option.value === SELECT_ALL_OPTION.value ? "#9C27B0" : "inherit",
                  fontWeight: option.value === SELECT_ALL_OPTION.value ? "500" : "normal",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#9C27B00A";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = option.value === SELECT_ALL_OPTION.value ? "#9C27B00A" : "inherit";
                }}
              >
                {option.label}
              </li>
            )}
            ListboxProps={{
              sx: {
                '& .MuiAutocomplete-option[aria-selected="true"]': {
                  backgroundColor: "#fbf7fc",

                  "&.Mui-focused": {
                    backgroundColor: "#f3edf8",
                  },
                },
                "& .MuiAutocomplete-option.Mui-focused": {
                  backgroundColor: "#fbf7fc",
                },
              },
            }}
          />
          <Button variant="contained" color="secondary" sx={{ textTransform: "none", height: 40, flexShrink: 0, minWidth: 120 }} data-tracker="423" onClick={() => { handleGetData() }}
            disabled={(fetchBy !== "Organization" && selectedOptions.length === 0)}>
            Get Data
          </Button>
        </Box>
        {loading ? (
          <SimpleBackdrop />
        ) : hasFetchedData ? (
          <ManualExpenseTable />
        ) : null}
      </Paper>
    </>
  )
}

export default ManualExpenses
